<template>
    <transition name="toast-slide">
        <div 
            v-if="show" 
            class="toast-notification" 
            :class="typeClass" 
            role="alert"
            :aria-live="ariaLive"
            :aria-atomic="true"
        >
            <div class="toast-icon-wrapper" :class="iconWrapperClass">
                <span class="material-symbols-outlined toast-icon" aria-hidden="true">
                    {{ iconName }}
                </span>
            </div>
            <div class="toast-content">
                <span class="toast-message">{{ message }}</span>
            </div>
            <button 
                class="toast-close" 
                @click="dismiss" 
                aria-label="Dismiss notification"
                :class="closeButtonClass"
            >
                <span class="material-symbols-outlined">close</span>
            </button>
        </div>
    </transition>
</template>

<script>
import { ToastType } from '../utils/toast';

export default {
    name: 'ToastNotification',
    computed: {
        show() {
            return this.$store.state.toast.show
        },
        message() {
            return this.$store.state.toast.message
        },
        type() {
            return this.$store.state.toast.type
        },
        typeClass() {
            return `toast-${this.type || ToastType.ERROR}`
        },
        iconName() {
            const icons = {
                [ToastType.SUCCESS]: 'check_circle',
                [ToastType.ERROR]: 'error',
                [ToastType.WARNING]: 'warning',
                [ToastType.INFO]: 'info'
            }
            return icons[this.type] || icons[ToastType.ERROR]
        },
        iconWrapperClass() {
            return `toast-icon-${this.type || ToastType.ERROR}`
        },
        closeButtonClass() {
            return `toast-close-${this.type || ToastType.ERROR}`
        },
        ariaLive() {
            return this.type === ToastType.ERROR ? 'assertive' : 'polite';
        },
        toastState() {
            return this.$store.state.toast;
        }
    },
    watch: {
        toastState: {
            deep: true,
            handler(val) {
                if (val.show) {
                    clearTimeout(this._timer)
                    this._timer = setTimeout(() => {
                        this.$store.commit('hideToast')
                    }, 5000)
                }
            }
        }
    },
    beforeUnmount() {
        clearTimeout(this._timer)
    },
    methods: {
        dismiss() {
            clearTimeout(this._timer)
            this.$store.commit('hideToast')
        }
    }
}
</script>

<style scoped>
.toast-notification {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 9999;
    min-width: 320px;
    max-width: 500px;
    padding: 14px 16px;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-shadow: 0 12px 32px rgba(52, 48, 43, 0.15);
    font-family: 'Manrope', sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
    border: 1px solid transparent;
}

.toast-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
}

.toast-icon {
    font-size: 1.25rem;
    font-weight: 600;
}

.toast-content {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
}

.toast-message {
    flex: 1;
    word-break: break-word;
    line-height: 1.5;
}

.toast-close {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.1rem;
    padding: 4px;
    opacity: 0.6;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toast-close:hover {
    opacity: 1;
}

.toast-close:focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
}

.toast-close:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
}

/* Success Toast - Green */
.toast-success {
    background: #f0fdf4;
    color: #166534;
    border-color: #bbf7d0;
}

.toast-icon-success {
    background: #dcfce7;
    color: #16a34a;
}

.toast-close-success {
    color: #166534;
}

.toast-close-success:hover {
    background: rgba(22, 101, 52, 0.1);
}

/* Error Toast - Red */
.toast-error {
    background: #fef2f2;
    color: #991b1b;
    border-color: #fecaca;
}

.toast-icon-error {
    background: #fee2e2;
    color: #dc2626;
}

.toast-close-error {
    color: #991b1b;
}

.toast-close-error:hover {
    background: rgba(153, 27, 27, 0.1);
}

/* Warning Toast - Amber */
.toast-warning {
    background: #fffbeb;
    color: #92400e;
    border-color: #fde68a;
}

.toast-icon-warning {
    background: #fef3c7;
    color: #d97706;
}

.toast-close-warning {
    color: #92400e;
}

.toast-close-warning:hover {
    background: rgba(146, 64, 14, 0.1);
}

/* Info Toast - Blue */
.toast-info {
    background: #eff6ff;
    color: #1e40af;
    border-color: #bfdbfe;
}

.toast-icon-info {
    background: #dbeafe;
    color: #2563eb;
}

.toast-close-info {
    color: #1e40af;
}

.toast-close-info:hover {
    background: rgba(30, 64, 175, 0.1);
}

/* Transitions */
.toast-slide-enter-active,
.toast-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-enter-from {
    opacity: 0;
    transform: translateX(100%);
}

.toast-slide-leave-to {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
}

/* Mobile Responsive */
@media (max-width: 768px) {
    .toast-notification {
        left: 8px;
        right: 8px;
        top: 60px;
        min-width: auto;
        max-width: none;
        padding: 12px 14px;
    }

    .toast-icon-wrapper {
        width: 32px;
        height: 32px;
    }

    .toast-icon {
        font-size: 1.1rem;
    }
}

/* Accessibility - Reduced Motion */
@media (prefers-reduced-motion: reduce) {
    .toast-slide-enter-active,
    .toast-slide-leave-active {
        transition: opacity 0.2s ease;
    }
    
    .toast-slide-enter-from,
    .toast-slide-leave-to {
        transform: none;
    }
}
</style>
